// src/components/Sales/SalesList.tsx

import React, { useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Box,
    Typography,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Collapse,
    Button,
    Divider,
    Tabs,
    Tab,
    Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DraftsIcon from '@mui/icons-material/Drafts';
import ErrorIcon from '@mui/icons-material/Error';
import { SalesResult, SalesStatus, formatCurrency, formatDate, statusDisplayMap, getStatusColor } from '../../types/sales';

interface SalesListProps {
    salesData: SalesResult[];
    onStatusChange: (id: number, newStatus: SalesStatus) => void;
    loading: boolean;
}

// 担当者ごとの売上データをグループ化する関数
const groupSalesByManager = (data: SalesResult[]) => {
    const managerGroups: Record<string, {
        managerName: string,
        count: number, 
        totalSales: number, 
        totalProfit: number,
        items: SalesResult[]
    }> = {};

    data.forEach(sale => {
        const managerName = sale.Member_Name || '未設定';
        // const profit = Number(sale.gross_profit) || 0;
        const profit = Number(sale.final_profit) || 0;
        
        if (!managerGroups[managerName]) {
            managerGroups[managerName] = {
                managerName,
                count: 0,
                totalSales: 0,
                totalProfit: 0,
                items: []
            };
        }
        
        managerGroups[managerName].count += 1;
        managerGroups[managerName].totalSales += Number(sale.sales_amount) || 0;
        
        // マイナスの利益は合計に含めない（0として扱う）
        if (profit > 0) {
            // console.log(`Profit for ${managerName}: ${profit}`);
            managerGroups[managerName].totalProfit += profit;
        }
        
        managerGroups[managerName].items.push(sale);
    });
    
    return Object.values(managerGroups).sort((a, b) => 
        a.managerName.localeCompare(b.managerName, 'ja')
    );
};

export const SalesList: React.FC<SalesListProps> = ({
    salesData,
    onStatusChange,
    loading
}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    const [tabValue, setTabValue] = useState(0);

    // 売上計上月と請求月でデータをグループ化
    const groupedSalesData = useMemo(() => {
        // 現在の計上月のデータ
        const currentMonthData: SalesResult[] = [];
        // 請求月別のデータ
        const billingMonthGroups: Record<string, SalesResult[]> = {};

        salesData.forEach(sales => {
            // sales_monthとbilling_monthの年月部分を取得
            const salesYearMonth = sales.sales_month.substring(0, 7);
            const billingYearMonth = sales.billing_month.substring(0, 7);

            if (salesYearMonth === billingYearMonth) {
                // 売上計上月と請求月が同じ場合は現在の計上月グループに追加
                currentMonthData.push(sales);
            } else {
                // 請求月が異なる場合は請求月グループに追加
                if (!billingMonthGroups[billingYearMonth]) {
                    billingMonthGroups[billingYearMonth] = [];
                }
                billingMonthGroups[billingYearMonth].push(sales);
            }
        });

        // 請求月グループを日付降順（新しい順）にソート
        const sortedBillingMonths = Object.keys(billingMonthGroups).sort().reverse();
        
        return {
            currentMonthData,
            billingMonthGroups,
            sortedBillingMonths
        };
    }, [salesData]);

    // 担当者別集計データの作成
    const managerSummaries = useMemo(() => {
        // すべてのデータを担当者別に集計
        return groupSalesByManager(salesData);
    }, [salesData]);

    // 行の展開・折りたたみを切り替え
    const handleToggleRow = (id: number) => {
        setExpandedRows(prev => {
            if (prev.includes(id)) {
                return prev.filter(rowId => rowId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // タブ切り替えの処理
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // ステータスチップの色とアイコンを取得
    const getStatusConfig = (status: SalesStatus) => {
        switch (status) {
            case SalesStatus.DRAFT:
                return {
                    color: 'default' as 'default',
                    bgColor: theme.palette.grey[300],
                    icon: <DraftsIcon fontSize="small" />,
                    label: '下書き'
                };
            case SalesStatus.READY:
                return {
                    color: 'primary' as 'primary',
                    bgColor: theme.palette.primary.main,
                    icon: <HourglassEmptyIcon fontSize="small" />,
                    label: '準備完了'
                };
            case SalesStatus.CONFIRMED:
                return {
                    color: 'success' as 'success',
                    bgColor: theme.palette.success.main,
                    icon: <CheckCircleIcon fontSize="small" />,
                    label: '確定済み'
                };
            default:
                return {
                    color: 'default' as 'default',
                    bgColor: theme.palette.grey[300],
                    icon: <DraftsIcon fontSize="small" />,
                    label: status
                };
        }
    };

    // ステータス変更ボタンを取得
    const getStatusChangeButtons = (item: SalesResult) => {
        const buttons = [];

        // 現在のステータスに応じて、次に遷移可能なステータスのボタンを表示
        switch (item.status) {
            case SalesStatus.DRAFT:
                buttons.push(
                    <Button
                        key="to-ready"
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => onStatusChange(item.id, SalesStatus.READY)}
                        sx={{ mr: 1 }}
                    >
                        準備完了にする
                    </Button>
                );
                break;
            case SalesStatus.READY:
                buttons.push(
                    <Button
                        key="to-confirmed"
                        variant="outlined"
                        color="success"
                        size="small"
                        onClick={() => onStatusChange(item.id, SalesStatus.CONFIRMED)}
                        sx={{ mr: 1 }}
                    >
                        確定する
                    </Button>
                );
                buttons.push(
                    <Button
                        key="to-draft"
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={() => onStatusChange(item.id, SalesStatus.DRAFT)}
                    >
                        下書きに戻す
                    </Button>
                );
                break;
            case SalesStatus.CONFIRMED:
                buttons.push(
                    <Button
                        key="to-ready"
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => onStatusChange(item.id, SalesStatus.READY)}
                    >
                        準備完了に戻す
                    </Button>
                );
                break;
        }

        return buttons;
    };

    // 日付を表示用に変換（YYYY-MM → YYYY年MM月）
    const formatYearMonth = (yearMonth: string): string => {
        const [year, month] = yearMonth.split('-');
        return `${year}年${parseInt(month)}月`;
    };

    // 担当者別集計カードをレンダリング
    const renderManagerSummaryCards = (managers: ReturnType<typeof groupSalesByManager>) => (
        <Box p={2}>
            {managers.length === 0 ? (
                <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                    該当データがありません
                </Typography>
            ) : (
                <Grid container spacing={2}>
                    {managers.map((manager, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {manager.managerName}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        工事件数: {manager.count}件
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        売上合計: {formatCurrency(manager.totalSales)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            粗利合計:
                                        </Typography>
                                        <Typography 
                                            variant="h5" 
                                            sx={{ 
                                                mt: 1, 
                                                color: manager.totalProfit < 0 ? 'error.main' : 'success.main'
                                            }}
                                        >
                                            {formatCurrency(manager.totalProfit)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );

    // モバイル表示用のカードリスト（単一グループ向け）
    const renderMobileCards = (data: SalesResult[], startIndex: number = 0) => (
        <Box>
            {data.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="textSecondary">売上データが見つかりません</Typography>
                </Paper>
            ) : (
                data.map((item, index) => {
                    const statusConfig = getStatusConfig(item.status as SalesStatus);
                    const isExpanded = expandedRows.includes(item.id);
                    // const grossProfit = Number(item.gross_profit);
                    const grossProfit = Number(item.final_profit);
                    const isNegative = grossProfit < 0;


                    return (
                        <Card
                            key={item.id}
                            elevation={2}
                            sx={{
                                mb: 2,
                                borderLeft: '4px solid',
                                borderColor: statusConfig.bgColor
                            }}
                        >
                            <CardContent sx={{ p: 2 }}>
                                <Box
                                    onClick={() => handleToggleRow(item.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {/* ヘッダー部分 */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Chip
                                            {...(statusConfig.icon ? { icon: statusConfig.icon } : {})}
                                            label={statusConfig.label}
                                            color={statusConfig.color}
                                            size="small"
                                            sx={{ height: '24px', fontSize: '0.75rem' }}
                                        />
                                        <Typography variant="body2" color="textSecondary">
                                            No: {startIndex + index + 1}
                                        </Typography>
                                    </Box>

                                    {/* 主要情報 */}
                                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500, mb: 1 }}>
                                        {item.Customer_Name || '(顧客なし)'}
                                    </Typography>

                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        {item.Site_Name || '(現場名なし)'}
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" color="textSecondary">担当:</Typography>
                                        <Typography variant="body2">{item.Member_Name || '-'}</Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" color="textSecondary">会社No:</Typography>
                                        <Typography variant="body2">{item.invoice_index || '-'}</Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" color="textSecondary">作業完了日:</Typography>
                                        <Typography variant="body2">{formatDate(item.completion_date) || '-'}</Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" color="textSecondary">見積金額:</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {formatCurrency(item.sales_amount)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">合計利益:</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight="500"
                                                color={isNegative ? 'error.main' : 'success.main'}
                                            >
                                                {formatCurrency(item.final_profit)}
                                            </Typography>
                                            {isNegative && (
                                                <Chip
                                                    label="赤字"
                                                    color="error"
                                                    size="small"
                                                    icon={<ErrorIcon fontSize="small" />}
                                                    sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mt: 1.5,
                                        color: 'primary.main'
                                    }}>
                                        <ExpandMoreIcon
                                            sx={{
                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                                transition: 'transform 0.3s',
                                                fontSize: '1.2rem'
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Collapse in={isExpanded}>
                                    <Divider sx={{ mt: 2, mb: 2 }} />

                                    <Typography variant="subtitle2" gutterBottom>費用内訳:</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="textSecondary">人件費:</Typography>
                                            <Typography variant="body2">{formatCurrency(item.labor_cost)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="textSecondary">車両費:</Typography>
                                            <Typography variant="body2">{formatCurrency(item.vehicle_cost)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="textSecondary">諸経費:</Typography>
                                            <Typography variant="body2">{formatCurrency(item.expense_cost)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="textSecondary">外注費:</Typography>
                                            <Typography variant="body2">{formatCurrency(item.vendor_cost)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="textSecondary">その他費用:</Typography>
                                            <Typography variant="body2">{formatCurrency(item.other_cost)}</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ mt: 2 }}>
                                        {getStatusChangeButtons(item)}
                                    </Box>
                                </Collapse>
                            </CardContent>
                        </Card>
                    );
                })
            )}
        </Box>
    );

    // デスクトップ表示用のテーブル（単一グループ向け）
    const renderDesktopTable = (data: SalesResult[], startIndex: number = 0) => (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>No</TableCell>
                        <TableCell>担当</TableCell>
                        <TableCell>会社名</TableCell>
                        <TableCell>会社No</TableCell>
                        <TableCell>現場名</TableCell>
                        <TableCell>作業完了日</TableCell>
                        <TableCell align="right">見積金額</TableCell>
                        <TableCell align="right">合計利益</TableCell>
                        <TableCell>マイナス</TableCell>
                        <TableCell>ステータス</TableCell>
                        <TableCell>操作</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={11} align="center">
                                売上データが見つかりません
                            </TableCell>
                        </TableRow>
                    ) : (
                        data.map((item, index) => {
                            // console.log("renderDesktopTable",item);
                            const statusConfig = getStatusConfig(item.status as SalesStatus);
                            const grossProfit = Number(item.final_profit);
                            const isNegative = grossProfit < 0;

                            return (
                                <TableRow
                                    key={item.id}
                                    sx={{
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                    }}
                                >
                                    <TableCell>{startIndex + index + 1}</TableCell>
                                    <TableCell>{item.Member_Name || '-'}</TableCell>
                                    <TableCell>{item.Customer_Name || '-'}</TableCell>
                                    <TableCell>{item.invoice_index || '-'}</TableCell>
                                    <TableCell>{item.Site_Name || '-'}</TableCell>
                                    <TableCell>{formatDate(item.completion_date)}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.sales_amount)}</TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            color: isNegative ? 'error.main' : 'success.main',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {formatCurrency(item.final_profit)}
                                    </TableCell>
                                    <TableCell>
                                        {isNegative && (
                                            <Chip
                                                label="赤字"
                                                color="error"
                                                size="small"
                                                icon={<ErrorIcon fontSize="small" />}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            {...(statusConfig.icon ? { icon: statusConfig.icon } : {})}
                                            label={statusConfig.label}
                                            color={statusConfig.color}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleRow(item.id)}
                                            color="primary"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // 売上データが空の場合
    if (salesData.length === 0) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center', mb: 3 }}>
                <Typography variant="body1" color="textSecondary">売上データがありません</Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ width: '100%', mb: 2 }}>
            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
            >
                <Tab label="担当者別集計" />
                <Tab label="詳細一覧" />
            </Tabs>

            {/* 担当者別集計 */}
            {tabValue === 0 && renderManagerSummaryCards(managerSummaries)}

            {/* 詳細一覧 */}
            {tabValue === 1 && (
                <Box>
                    {isSmallScreen ? (
                        // モバイル表示
                        <Box>
                            {/* 現在の計上月のデータ */}
                            {renderMobileCards(groupedSalesData.currentMonthData)}
                            
                            {/* 請求月が異なるデータのグループ */}
                            {groupedSalesData.sortedBillingMonths.map(billingMonth => (
                                <React.Fragment key={billingMonth}>
                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="h6" sx={{ px: 2, mb: 2, fontWeight: 500 }}>
                                        {formatYearMonth(billingMonth)}分として
                                    </Typography>
                                    {renderMobileCards(groupedSalesData.billingMonthGroups[billingMonth], groupedSalesData.currentMonthData.length)}
                                </React.Fragment>
                            ))}
                        </Box>
                    ) : (
                        // デスクトップ表示
                        <Box>
                            {/* 現在の計上月のデータ */}
                            {renderDesktopTable(groupedSalesData.currentMonthData)}
                            
                            {/* 請求月が異なるデータのグループ */}
                            {groupedSalesData.sortedBillingMonths.map(billingMonth => (
                                <React.Fragment key={billingMonth}>
                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                                        {formatYearMonth(billingMonth)}分として
                                    </Typography>
                                    {renderDesktopTable(groupedSalesData.billingMonthGroups[billingMonth], groupedSalesData.currentMonthData.length)}
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
};